import { citationStyles } from './citation-styles';
import {CitationCredit} from './SoftwareAcknowledgements';
import { AccountSelect } from './AccountSelect';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowRight,
  Check,
  Download,
  Monitor,
  ShieldCheck,
} from 'lucide-react';
import { App } from './App';
import { EducatorInterest } from './EducatorInterest';
import { EducatorPortal } from './EducatorPortal';
import { api, ApiError, setCsrfToken, setProfileId } from './api';
import { ErrorNotice, Spinner } from './components';
import './account.css';
import { AccountSettings, PasswordRecovery, VerifyEmail } from './AccountSecurity';
import { passkeySignIn, passkeyError } from './Passkeys';
import { AccountVoiceField, clearSavedProfileVoice } from './AccountVoiceField';
import { currentStudyTypes } from '../../../packages/shared/study-types.mjs';

const responseLocales = [
  ['en-GB', 'English (UK)'],
  ['en-US', 'English (US)'],
  ['en-IE', 'English (Ireland)'],
  ['en-CA', 'English (Canada)'],
  ['en-AU', 'English (Australia)'],
  ['en-NZ', 'English (New Zealand)'],
  ['en-ZA', 'English (South Africa)'],
] as const;
type ResponseLocale = (typeof responseLocales)[number][0];
type StudentDetails = {
  currentStudyType?: string | null;
  name: string;
  studyGoal: string;
  responseLocale?: ResponseLocale;
  responseLocaleRevision?: number;
  citationStyle?: keyof typeof citationStyles;
  citationStyleName?: string;
  citationGuideUrl?: string;
  citationPreferenceRevision?: number;
};
type ProfileDetails = StudentDetails & {
  responseLocale: ResponseLocale;
  responseLocaleRevision: number;
};
function profileDetails(profile: StudentDetails | null, currentStudyType?: string | null): ProfileDetails {
  return {
    currentStudyType: currentStudyType ?? null,
    name: profile?.name ?? '',
    studyGoal: profile?.studyGoal ?? '',
    responseLocale: responseLocales.some(([value]) => value === profile?.responseLocale)
      ? profile!.responseLocale!
      : 'en-GB',
    responseLocaleRevision:
      Number.isInteger(profile?.responseLocaleRevision) && profile!.responseLocaleRevision! >= 0
        ? profile!.responseLocaleRevision!
        : 0,
    citationStyle: profile?.citationStyle ?? 'unconfirmed',
    citationStyleName: profile?.citationStyleName ?? '',
    citationGuideUrl: profile?.citationGuideUrl ?? '',
    citationPreferenceRevision: profile?.citationPreferenceRevision ?? 0,
  };
}
const responseLocaleLabel = (locale: ResponseLocale) =>
  responseLocales.find(([value]) => value === locale)![1];
export type User = {
  currentStudyType?: string | null;
  id: string;
  name: string;
  email: string;
  initials: string;
  emailVerified: boolean;
  pendingEmail?: string | null;
  verificationSentAt?: string | null;
  verificationDelivery?: string | null;
  profileComplete: boolean;
  profile: StudentDetails | null;
  legal?: LegalDocuments | null;
};
type LegalDocument = { version: string; title: string; url: string };
type LegalDocuments = { required: boolean; terms: LegalDocument; privacy: LegalDocument; acceptedAt: string | null };
export type AccountState = {
  user: User | null;
  csrfToken: string | null;
  authMode: string;
  verificationRequired?: boolean;
};
type DownloadInfo = {
  windows: {
    available: boolean;
    version?: string;
    bytes?: number;
    sha256?: string;
    url?: string;
    localServiceRequired?: boolean;
    format?: 'installer' | 'zip';
    automaticUpdates?: boolean;
    unsigned?: boolean;
  };
};

function Brand() {
  return (
    <a className="account-brand" href="/" aria-label="Marvin website">
      Marvin<span>by Immerso Gnosis</span>
    </a>
  );
}
function Header({ user }: { user?: User | null }) {
  return (
    <header className="account-header">
      <Brand />
      <nav aria-label="Account navigation">
        <a href="/">Marvin website</a>
        {user ? <a href="/home">My home</a> : <a href="/signin">Sign in</a>}
        {user && <a href="/account">My account</a>}
      </nav>
    </header>
  );
}

export function AccountJourney() {
  const [educatorEntry] = useState(() => location.pathname.replace(/\/$/, '') === '/educator' || new URLSearchParams(location.search).get('next') === 'educator');
  const [state, setState] = useState<AccountState | null>(null),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const load = () => {
    setError('');
    api<AccountState>('/account/session')
      .then((value) => {
        setCsrfToken(value.csrfToken);
        if (value.user) setProfileId(value.user.id, value.user.email);
        setState(value);
      })
      .catch((e) => setError(e.message));
  };
  useEffect(load, []);
  // The server refuses work until changed terms are accepted; reload so the terms step shows.
  useEffect(() => {
    window.addEventListener('marvin-terms-required', load);
    return () => window.removeEventListener('marvin-terms-required', load);
  }, []);
  if (location.pathname.replace(/\/$/, '') === '/educator/register-interest') return <EducatorInterest />;
  if (!state)
    return (
      <div className="account-shell">
        <Header />
        {error ? <ErrorNotice message={error} retry={load} /> : <Spinner label="Opening Marvin…" />}
      </div>
    );
  const path = location.pathname.replace(/\/$/, '') || '/';
  function signedIn(value: AccountState) {
    setCsrfToken(value.csrfToken);
    setProfileId(value.user!.id, value.user!.email);
    setState(value);
    setNotice('');
    history.replaceState(
      null,
      '',
      (value.verificationRequired || educatorEntry) && !value.user!.emailVerified
          ? '/verify-email'
          : educatorEntry ? '/educator'
          : value.user!.profileComplete
            ? '/home'
            : '/profile',
    );
  }
  function ended(message: string) {
    window.dispatchEvent(new Event('ig-account-ended'));
    setCsrfToken(null);
    setState({ ...state!, user: null });
    setNotice(message);
    history.replaceState(
      null,
      '',
      educatorEntry ? '/educator' : '/signin',
    );
  }
  async function signOut() {
    await api('/account/logout', 'POST', {});
    ended('You have signed out.');
  }
  if (path === '/' && !state.user && state.authMode === 'development') return <App />;
  if (educatorEntry && state.user?.emailVerified && path !== '/account')
    return <EducatorPortal user={state.user} onSignOut={signOut} />;
  if (path === '/downloads')
    return (
      <div className="account-shell">
        <Header user={state.user} />
        <main className="account-download-page">
          <p className="eyebrow">TAKE YOUR WORK WITH YOU</p>
          <h1>Download Marvin</h1>
          <DownloadPanel />
          <p>
            <a href={state.user ? '/home' : '/signup'}>
              {state.user ? 'Back to my home' : 'Create your student account'}
            </a>
          </p>
        </main>
      </div>
    );
  if (
    state.user?.profileComplete &&
    !state.user.legal?.required &&
    (!state.verificationRequired || state.user.emailVerified) &&
    !['/account', '/profile', '/forgot-password', '/reset-password'].includes(path)
  )
    return <App />;
  return (
    <div className="account-shell">
      <Header user={state.user} />
      <main id="account-content">
        {notice && (
          <p role="status" className="account-notice">
            {notice}
          </p>
        )}
        {path === '/forgot-password' || path === '/reset-password' ? (
          <PasswordRecovery
            signInUrl={educatorEntry ? '/educator' : '/signin'}
          />
        ) : !state.user ? (
          <SignUp
            signIn={path !== '/signup'}
            onSignedIn={signedIn}
            educatorEntry={educatorEntry}
          />
        ) : path === '/account' ? (
          <AccountSettings
            user={state.user}
            onChanged={(user) => setState({ ...state, user })}
            onVerified={signedIn}
            onEnded={ended}
          />
        ) : (state.verificationRequired || educatorEntry) && !state.user.emailVerified ? (
          <VerifyEmail
            user={state.user}
            onChanged={(user) => setState({ ...state, user })}
            onVerified={signedIn}
            onSignOut={() => void signOut().catch((e) => setNotice(e.message))}
          />
        ) : !state.user.profileComplete || path === '/profile' ? (
          <ProfileForm
            user={state.user}
            onSaved={(user) => {
              setProfileId(user.id, user.email);
              setState({ ...state, user });
              history.replaceState(null, '', '/home');
            }}
          />
        ) : state.user.legal?.required ? (
          <AcceptTerms legal={state.user.legal} onAccepted={(user) => setState({ ...state, user })} />
        ) : (
          <App />
        )}
      </main>
      <footer className="account-footer">
        Your ideas. Your work. A record of how it came together.
        <a href="/product.html#privacy">How Marvin handles your work</a>
        <CitationCredit/>
      </footer>
    </div>
  );
}

function SignUp({
  signIn,
  onSignedIn,
  educatorEntry = false,
}: {
  signIn: boolean;
  onSignedIn: (value: AccountState) => void;
  educatorEntry?: boolean;
}) {
  const [email, setEmail] = useState(''),
    [password, setPassword] = useState(''),
    [confirm, setConfirm] = useState(''),
    [show, setShow] = useState(false),
    [rememberSignIn, setRememberSignIn] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function submit(event: FormEvent) {
    event.preventDefault();
    // Read the form itself: password managers can fill without React change events.
    const fields = new FormData(event.currentTarget as HTMLFormElement);
    const submittedEmail = String(fields.get('email') ?? '');
    const submittedPassword = String(fields.get('password') ?? '');
    setError('');
    if (!signIn && submittedPassword !== String(fields.get('confirmPassword') ?? '')) {
      setError('The passwords do not match. Check them and try again.');
      return;
    }
    setBusy(true);
    try {
      const result = await api<AccountState>(`/account/${signIn ? 'login' : 'register'}`, 'POST', {
        email: submittedEmail,
        password: submittedPassword,
        rememberSignIn,
      });
      setPassword('');
      setConfirm('');
      onSignedIn(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="account-grid">
      <section className="account-intro">
        {educatorEntry ? <>
          <p className="eyebrow">MARVIN FOR EDUCATORS</p>
          <h1>Your institution’s teaching space.</h1>
          <p className="account-lede">Sign in with the email invited by your institution. Educator access requires institutional approval and an explicit invitation.</p>
          <h2>Interested in bringing Marvin to your institution?</h2>
          <p>Tell us about your teaching needs. Registering interest does not create an account or grant access.</p>
          <a className="button primary" href="/educator/register-interest">Register interest</a>
        </> : <>
        <p className="eyebrow">YOUR STUDENT WORKSPACE</p>
        <h1>{signIn ? 'Welcome back.' : 'Make room for your own thinking.'}</h1>
        <p className="account-lede">
          Bring your research, drafts and AI guidance together. Keep a clear record of how your work
          develops.
        </p>
        <ol className="account-steps">
          <li className="current">
            <span>1</span>Create your account
          </li>
          <li>
            <span>2</span>Make it yours
          </li>
          <li>
            <span>3</span>Start your workspace
          </li>
        </ol>
        <p className="account-note">
          <ShieldCheck size={21} />
          Your work stays in your account. A learning record documents your process without claiming
          proof of exclusive authorship.
        </p>
        </>}
      </section>
      <section className="account-form-card" aria-labelledby="account-form-title">
        <p className="eyebrow">{educatorEntry ? 'EDUCATOR ACCOUNT' : signIn ? 'YOUR ACCOUNT' : 'STEP 1 OF 3'}</p>
        <h2 id="account-form-title">{signIn ? 'Sign in to Marvin' : 'Create your account'}</h2>
        <p>
          {educatorEntry ? 'Use the email address on your institution invitation.' : signIn
            ? 'Continue where you left off.'
            : 'A few details now. Your own space to learn next.'}
        </p>
        <form onSubmit={submit} method="post">
          <label htmlFor="account-email">Email address</label>
          <input
            id="account-email"
            name="email"
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <label htmlFor="account-password">Password</label>
          <input
            id="account-password"
            name="password"
            type={show ? 'text' : 'password'}
            autoComplete={signIn ? 'current-password' : 'new-password'}
            minLength={signIn ? 1 : 10}
            maxLength={256}
            required
            aria-describedby="password-hint"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p id="password-hint" className="account-hint">
            {signIn
              ? 'Use the password you chose when creating your Marvin account.'
              : 'Use at least 10 characters. A few unrelated words work well.'}
          </p>
          {!signIn && (
            <>
              <label htmlFor="account-confirm">Confirm password</label>
              <input
                id="account-confirm"
                name="confirmPassword"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-describedby="account-confirm-state"
              />
              <p id="account-confirm-state" className="account-hint" role="status">
                {confirm && confirm !== password ? 'The passwords do not match yet.' : confirm ? 'The passwords match.' : ''}
              </p>
            </>
          )}
          <label className="account-checkbox">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            Show password{!signIn && 's'}
          </label>
          <label className="account-checkbox">
            <input
              name="rememberSignIn"
              type="checkbox"
              checked={rememberSignIn}
              onChange={(e) => setRememberSignIn(e.target.checked)}
            />
            Keep me signed in on this device
          </label>
          <p className="account-hint">
            For up to 30 days. Leave this off on a shared computer. You can use your password
            manager to fill these fields.
          </p>
          {error && <ErrorNotice message={error} />}
          <button type="submit" className="button primary account-submit" disabled={busy}>
            {busy ? 'Please wait…' : signIn ? 'Sign in' : 'Create account'}
            <ArrowRight size={18} />
          </button>
          {signIn && (
            <button
              type="button"
              className="button secondary account-submit"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError('');
                try {
                  onSignedIn(await passkeySignIn(rememberSignIn));
                } catch (e) {
                  setError(passkeyError(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Sign in with a passkey
            </button>
          )}
          <p className="account-hint">
            {signIn ? educatorEntry ? 'Have an institution invitation?' : 'New to Marvin?' : 'Already have an account?'}{' '}
            <a
              href={`${signIn ? '/signup' : '/signin'}${educatorEntry ? '?next=educator' : ''}`}
            >
              {signIn ? 'Create an account' : 'Sign in'}
            </a>
          </p>
          {signIn && (
            <p>
              <a
                href={`/forgot-password${educatorEntry ? '?next=educator' : ''}`}
              >
                Forgot your password?
              </a>
            </p>
          )}
        </form>
      </section>
    </div>
  );
}

function AcceptTerms({ legal, onAccepted }: { legal: LegalDocuments; onAccepted: (user: User) => void }) {
  const [agreed, setAgreed] = useState(false),
    [ageConfirmed, setAgeConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function accept(event: FormEvent) {
    event.preventDefault();
    if (busy || !agreed || !ageConfirmed) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ user: User }>('/account/legal-acceptance', 'POST', {
        terms: legal.terms.version,
        privacy: legal.privacy.version,
        ageConfirmed: true,
      });
      onAccepted(result.user);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="account-grid">
      <section className="account-intro">
        <p className="eyebrow">ONE LAST STEP</p>
        <h1>Before you start</h1>
        <p className="account-lede">Read Marvin’s terms and privacy policy. You only need to do this once.</p>
      </section>
      <section className="account-form-card">
        <form onSubmit={accept}>
          <fieldset className="account-fields" disabled={busy}>
            <ul className="account-documents">
              <li><a href={legal.terms.url} target="_blank" rel="noreferrer">{legal.terms.title}</a></li>
              <li><a href={legal.privacy.url} target="_blank" rel="noreferrer">{legal.privacy.title}</a></li>
            </ul>
            <label className="account-checkbox">
              <input id="account-agree" type="checkbox" autoFocus checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              I have read and agree to the terms and conditions and the privacy policy
            </label>
            <label className="account-checkbox">
              <input id="account-age" type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} />
              I am 13 or over, and if I am under 18 my parent or guardian agrees to these terms
            </label>
            {error && <ErrorNotice message={error} />}
            <button type="submit" className="button primary account-submit" disabled={busy || !agreed || !ageConfirmed}>
              {busy ? 'Please wait…' : 'Agree and continue'}
              <ArrowRight size={18} />
            </button>
          </fieldset>
        </form>
      </section>
    </div>
  );
}

export function ProfileForm({
  user,
  onSaved,
  onCancel,
  onBusyChange,
  onEdit,
  onDirtyChange,
  responseLocaleAvailable = true,
  citationPreferenceAvailable = true,
  embedded = false,
  nativeVoice = false,
  nativeVoiceAvailable = false,
}: {
  user: User;
  onSaved: (user: User) => void;
  onCancel?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onEdit?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  responseLocaleAvailable?: boolean;
  citationPreferenceAvailable?: boolean;
  embedded?: boolean;
  nativeVoice?: boolean;
  nativeVoiceAvailable?: boolean;
}) {
  const [details, setDetails] = useState<ProfileDetails>(() => profileDetails(user.profile, user.currentStudyType)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [localeConflict, setLocaleConflict] = useState(false),
    [savedLocaleReview, setSavedLocaleReview] = useState<ProfileDetails | null>(null);
  const savedDetails = useRef(details);
  const dirty =
    details.currentStudyType !== savedDetails.current.currentStudyType ||
    details.name !== savedDetails.current.name ||
    details.studyGoal !== savedDetails.current.studyGoal ||
    details.responseLocale !== savedDetails.current.responseLocale ||
    ['citationStyle', 'citationStyleName', 'citationGuideUrl'].some(
      (key) =>
        details[key as keyof ProfileDetails] !== savedDetails.current[key as keyof ProfileDetails],
    );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const change = <K extends keyof ProfileDetails>(key: K, value: ProfileDetails[K]) => {
    onEdit?.();
    setDetails((old) => ({ ...old, [key]: value }));
  };
  async function reviewSavedLocale() {
    if (busy) return;
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      const current = await api<AccountState>('/account/session');
      if (current.user?.id !== user.id)
        throw new Error(
          'Your session has changed. Sign in to this account again before saving. Your edits remain here.',
        );
      setSavedLocaleReview(profileDetails(current.user.profile, current.user.currentStudyType));
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  function resolveLocaleConflict(useSaved: boolean) {
    if (busy || !savedLocaleReview) return;
    setDetails((old) => ({
      ...old,
      responseLocale: useSaved ? savedLocaleReview.responseLocale : old.responseLocale,
      responseLocaleRevision: savedLocaleReview.responseLocaleRevision,
      citationStyle: useSaved ? savedLocaleReview.citationStyle : old.citationStyle,
      citationStyleName: useSaved ? savedLocaleReview.citationStyleName : old.citationStyleName,
      citationGuideUrl: useSaved ? savedLocaleReview.citationGuideUrl : old.citationGuideUrl,
      citationPreferenceRevision: savedLocaleReview.citationPreferenceRevision,
    }));
    setLocaleConflict(false);
    setSavedLocaleReview(null);
    setError('');
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy || localeConflict) return;
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      const payload = {
        name: details.name,
        studyGoal: details.studyGoal,
        currentStudyType: details.currentStudyType,
        ...(responseLocaleAvailable
          ? {
              responseLocale: details.responseLocale,
              responseLocaleRevision: details.responseLocaleRevision,
            }
          : {}),
        ...(citationPreferenceAvailable
          ? {
              citationStyle: details.citationStyle,
              citationStyleName: details.citationStyleName,
              citationGuideUrl: details.citationGuideUrl,
              citationPreferenceRevision: details.citationPreferenceRevision,
            }
          : {}),
      };
      const updated = (await api<{ user: User }>('/account/profile', 'PATCH', payload)).user;
      const saved = profileDetails(updated.profile, updated.currentStudyType);
      clearSavedProfileVoice(user.id, saved);
      savedDetails.current = saved;
      setDetails(saved);
      setSavedLocaleReview(null);
      onSaved(updated);
    } catch (e) {
      setError((e as Error).message);
      if (
        e instanceof ApiError &&
        e.status === 409 &&
        (responseLocaleAvailable || citationPreferenceAvailable)
      )
        setLocaleConflict(true);
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  return (
    <div className={embedded ? 'desktop-profile' : 'account-grid'}>
      {!embedded && (
        <section className="account-intro">
          <p className="eyebrow">MAKE IT YOURS</p>
          <h1>A workspace that starts with you.</h1>
          <p className="account-lede">
            Choose the name Marvin uses. Add your courses and education providers in My studies once
            you’re signed in.
          </p>
          <ol className="account-steps">
            <li className="done">
              <span>
                <Check size={17} />
              </span>
              Account created
            </li>
            <li className="current">
              <span>2</span>Your student profile
            </li>
            <li>
              <span>3</span>Your home
            </li>
          </ol>
          <p className="account-note">
            Your account stays with you as you move between school, college, university and other
            studies.
          </p>
        </section>
      )}
      <section className="account-form-card">
        {!embedded && (
          <>
            <p className="eyebrow">{user.profileComplete ? 'YOUR DETAILS' : 'STEP 2 OF 3'}</p>
            <h2>
              {user.profileComplete ? 'Edit your student profile' : 'Complete your student profile'}
            </h2>
            <p>Signed in as {user.email}</p>
          </>
        )}
        <form onSubmit={save}>
          <fieldset className="account-fields" disabled={busy}>
            {embedded && <h2 className="account-section-title">Personal details</h2>}
            <div className="profile-field account-profile-name">
              <label htmlFor="student-name">
                {embedded ? 'Preferred name' : 'What should we call you?'}
              </label>
              <input
                id="student-name"
                autoComplete="given-name"
                required
                maxLength={80}
                value={details.name}
                onChange={(e) => change('name', e.target.value)}
              />
              <AccountVoiceField userId={user.id} field="name" inputId="student-name"
                disabled={busy || !user.emailVerified} native={nativeVoice} nativeAvailable={nativeVoiceAvailable}
                onChange={value => change('name', value)}/>
            </div>
            <p className="account-hint">
              Your courses, education providers and study levels are in My studies. Keep the same
              Marvin account as your studies change.
            </p>
            <div className="profile-field">
              <label htmlFor="student-study-type">Current study type (optional)</label>
              <AccountSelect hosted={embedded} id="student-study-type" value={details.currentStudyType ?? ''} onValueChange={value=>change('currentStudyType',value || null)}>
                <option value="">Prefer not to say / choose later</option>
                {Object.entries(currentStudyTypes).map(([value,label])=><option key={value} value={value}>{label}</option>)}
              </AccountSelect>
            </div>
            <div className="profile-field">
              <label htmlFor="student-response-locale">Marvin language and region</label>
              <AccountSelect
                hosted={embedded}
                id="student-response-locale"
                value={details.responseLocale}
                disabled={!responseLocaleAvailable}
                aria-describedby={`student-response-locale-hint${responseLocaleAvailable ? '' : ' student-response-locale-unavailable'}`}
                onValueChange={(value) => change('responseLocale', value as ResponseLocale)}
              >
                {responseLocales.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </AccountSelect>
              <p id="student-response-locale-hint" className="account-hint">
                Choose the English wording and spelling Marvin uses. This does not change your
                course or assessment rules.
              </p>
              {!responseLocaleAvailable && (
                <p id="student-response-locale-unavailable" className="account-hint">
                  Update Marvin to change this setting. You can still save your name and study goal.
                </p>
              )}
            </div>
            <div className="profile-field">
              <label htmlFor="student-citation-style">Default citation style</label>
              <AccountSelect
                hosted={embedded}
                id="student-citation-style"
                value={details.citationStyle}
                disabled={!citationPreferenceAvailable}
                onValueChange={(value) => {
                  change('citationStyle', value as StudentDetails['citationStyle']);
                  change(
                    'citationStyleName',
                    !['unconfirmed', 'custom'].includes(value)
                      ? citationStyles[value as keyof typeof citationStyles]
                      : '',
                  );
                }}
              >
                {Object.entries(citationStyles).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </AccountSelect>
              <p className="account-hint">
                Used for new assignments. Change an assignment’s style in Plan → Settings to follow
                your module instructions. Existing assignments and citations keep their saved style.
              </p>
              {!citationPreferenceAvailable && (
                <p className="account-hint">Update Marvin to change your citation preference.</p>
              )}
            </div>
            {details.citationStyle === 'custom' && (
              <div className="profile-field">
                <label htmlFor="student-citation-name">Citation style name</label>
                <input
                  id="student-citation-name"
                  required
                  maxLength={120}
                  disabled={!citationPreferenceAvailable}
                  value={details.citationStyleName}
                  placeholder="For example, your school’s own guide"
                  onChange={(e) => change('citationStyleName', e.target.value)}
                />
                <AccountVoiceField userId={user.id} field="citationStyleName" inputId="student-citation-name"
                  disabled={busy || !user.emailVerified || !citationPreferenceAvailable} native={nativeVoice} nativeAvailable={nativeVoiceAvailable}
                  onChange={value => change('citationStyleName', value)}/>
              </div>
            )}
            <div className="profile-field">
              <label htmlFor="student-citation-guide">
                Link to your course’s referencing guide (optional)
              </label>
              <input
                id="student-citation-guide"
                type="url"
                maxLength={2000}
                aria-describedby="student-citation-guide-hint"
                disabled={!citationPreferenceAvailable}
                value={details.citationGuideUrl}
                onChange={(e) => change('citationGuideUrl', e.target.value)}
              />
              <p id="student-citation-guide-hint" className="account-hint">
                If your course or library provides referencing instructions, paste their web address
                here to keep it with new assignments. Leave this blank if you do not have a guide.
                The link does not change citation formatting.
              </p>
            </div>
            <div className="profile-field">
              <label htmlFor="student-goal">
                What would you like to work on? <span className="account-hint">Optional</span>
              </label>
              <textarea
                id="student-goal"
                rows={3}
                maxLength={600}
                placeholder="For example, keeping better research notes."
                value={details.studyGoal}
                onChange={(e) => change('studyGoal', e.target.value)}
              />
              <AccountVoiceField userId={user.id} field="studyGoal" inputId="student-goal"
                disabled={busy || !user.emailVerified} native={nativeVoice} nativeAvailable={nativeVoiceAvailable}
                onChange={value => change('studyGoal', value)}/>
              <p className="account-hint">
                Keep this about your studies. No sensitive personal details are needed.
              </p>
            </div>
            {error && <ErrorNotice message={error} />}
            {localeConflict && (
              <div className="profile-field" role="status">
                {savedLocaleReview ? (
                  <>
                    <p>
                      The saved choice is {responseLocaleLabel(savedLocaleReview.responseLocale)}.
                      Your choice is {responseLocaleLabel(details.responseLocale)}. Choose which to
                      use, then save your profile. Your other edits are kept.
                    </p>
                    <p>
                      Saved citation style:{' '}
                      {savedLocaleReview.citationStyleName || 'Choose for each assignment'} (
                      {savedLocaleReview.citationGuideUrl || 'no guide'}). Your citation style:{' '}
                      {details.citationStyleName || 'Choose for each assignment'} (
                      {details.citationGuideUrl || 'no guide'}).
                    </p>
                    <div className="profile-form-actions">
                      <button
                        type="button"
                        className="account-text-button"
                        onClick={() => resolveLocaleConflict(false)}
                      >
                        Keep my choice
                      </button>
                      <button
                        type="button"
                        className="account-text-button"
                        onClick={() => resolveLocaleConflict(true)}
                      >
                        Use saved choice
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p>
                      Your edits are kept. Review the saved language and citation preferences before
                      trying again.
                    </p>
                    <button
                      type="button"
                      className="account-text-button"
                      onClick={() => void reviewSavedLocale()}
                    >
                      Review saved preferences
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="profile-form-actions">
              <button
                className="button primary account-submit"
                disabled={busy || localeConflict}
                title={localeConflict ? 'Review the saved preferences before saving.' : undefined}
              >
                {busy
                  ? 'Saving…'
                  : user.profileComplete
                    ? 'Save profile'
                    : 'Save and go to my home'}
                {!embedded && <ArrowRight size={18} />}
              </button>
              {user.profileComplete &&
                (onCancel ? (
                  <button
                    type="button"
                    className="account-text-button"
                    onClick={onCancel}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                ) : (
                  <a href="/home">Cancel</a>
                ))}
            </div>
          </fieldset>
        </form>
      </section>
    </div>
  );
}

export function DownloadPanel() {
  const [info, setInfo] = useState<DownloadInfo | null>(null),
    [error, setError] = useState('');
  const load = () => {
    setError('');
    api<DownloadInfo>('/downloads')
      .then(setInfo)
      .catch((e) => setError(e.message));
  };
  useEffect(load, []);
  const windows = info?.windows;
  return (
    <section className="account-download">
      <div className="account-download-icon">
        <Monitor size={26} />
      </div>
      <div>
        <p className="eyebrow">MARVIN ON YOUR DEVICES</p>
        <h2>Your workspace, on your device.</h2>
        <p>Marvin is available for Windows, with macOS coming soon. Choose an available download below.</p>
        <p>
          iOS and Android tablet apps are outside the first release. On a managed device, check
          installation arrangements with your IT team.
        </p>
        <p>
          <a href="/product.html#devices">Device, offline and export limits</a> ·{' '}
          <a href="/product.html#support">Support and your account</a>
        </p>
        {error ? (
          <ErrorNotice message={error} retry={load} />
        ) : !windows ? (
          <Spinner label="Checking the download…" />
        ) : windows.available ? (
          <>
            <p className="account-hint">
              Windows x64 · Version {windows.version} ·{' '}
              {((windows.bytes || 0) / 1024 / 1024).toFixed(1)} MB ·{' '}
              {windows.format === 'installer' ? 'Windows installer' : 'ZIP download'}
            </p>
            <a className="button primary" href={windows.url} download>
              <Download size={18} />
              Download for Windows
            </a>
            <p className="account-hint">
              {windows.format === 'installer'
                ? 'Run the installer, then open Marvin from the Start menu. Sign in with the same account.'
                : 'Extract the complete folder, then open Marvin. Sign in with the same account.'}
            </p>
            <p className="account-pilot-note">
              {windows.localServiceRequired
                ? 'The Marvin service needs to be running on this computer. '
                : 'Sign in using your account from this website. '}
              {windows.unsigned !== false && 'This Windows package is not code-signed.'}
            </p>
            {windows.automaticUpdates && (
              <p className="account-hint">
                Marvin checks for updates automatically and downloads them in the background.
                Updates install when you next open the app. If you used the earlier ZIP, run this
                installer once to enable automatic updates.
              </p>
            )}
            <details>
              <summary>Download details</summary>
              <p className="account-hint">SHA-256 checksum</p>
              <code>{windows.sha256}</code>
              <p className="account-hint">
                macOS downloads are being prepared. You can manage studies, create assignments and
                view existing assignments read-only in the web portal on a supported browser.
              </p>
            </details>
          </>
        ) : (
          <p>The Windows download is being prepared. You can manage studies and view assignments in the web portal now.</p>
        )}
      </div>
    </section>
  );
}
